/**
 * Levelling control centre — per-status row action model (shared).
 *
 * Returns { primary, more } for a role row: the primary button plus overflow
 * menu items, driven by the evaluation lifecycle state. Action `act` keys are
 * abstract; the host portal maps them to navigation/emits and should hide any
 * act it has no handler for (e.g. `invite` / `remind` / `review` before the
 * backend supports those flows):
 *
 *   set-level  start a classification for an unlevelled role
 *   evaluate   start / re-run a full evaluation
 *   reclassify re-run a classification
 *   continue   resume an in-progress draft
 *   edit       edit an approved level
 *   review     open approve / send-back sign-off
 *   outcome    open the read-only outcome (classification/evaluation) summary
 *   invite     invite / re-assign a colleague
 *   remind     nudge the invited evaluator
 *   view-doc   open the job profile document
 *   anchor           set / remove the anchor mark (confirms through a modal, never
 *                    a silent toggle) — anchor roles, PD-1258
 *   anchor-evaluate  run a full evaluation with the anchor intent pre-set: the
 *                    route for anchoring a classified or not-yet-levelled role
 *   ceiling          set / remove the CEO ceiling on an existing anchor — the ONLY
 *                    route to it (the design prototype offers the ceiling from a
 *                    role's record, never during the levelling journey, because it
 *                    layers on a confirmed anchor and so needs the evaluation first)
 *
 * Icons: `mdi` for rm-portal (Vuetify), `icon` (Material Symbols) for rm-portal-beta.
 *
 * @param {string} status
 * @param {Object} [options]
 * @param {string}  [options.method]     'classify' | 'evaluate'
 * @param {boolean} [options.isAnchor]   role already carries the anchor mark
 * @param {boolean} [options.canAnchor]  caller may govern anchors (feature on + role allows it)
 * @param {boolean} [options.isCeiling]  this role currently holds the CEO ceiling
 * @param {boolean} [options.hasCeiling] a ceiling exists somewhere in the client
 */
export function levellingRowActions(status, options) {
  const method = (options && options.method) || null;
  const canAnchor = !!(options && options.canAnchor);
  const isAnchor = !!(options && options.isAnchor);
  const isCeiling = !!(options && options.isCeiling);
  const hasCeiling = !!(options && options.hasCeiling);

  /* ---- Anchor roles (PD-1258) ------------------------------------------------
     REMOVAL is offered on every status, not just `levelled`. An anchor deliberately
     survives re-levelling (it lives in document_lf_anchor, not on the evaluation
     row), so an anchored role can legitimately sit in-progress, in review or
     sent-back — and the server permits removal unconditionally, guarding only the
     paths that CREATE an anchor. Restricting the menu item to `levelled` left those
     roles with a mark nobody could take off.

     SETTING one still needs a completed full evaluation, which is what the
     `levelled` + `method === 'evaluate'` branch below expresses. */
  const removeAnchorItem = !canAnchor || !isAnchor
    ? []
    : [{ label: 'Remove anchor status', act: 'anchor', mdi: 'mdi-anchor', icon: 'anchor' }];
  /* The CEO ceiling layers ON an anchor, so it only appears on a role that already
     carries the mark. Once a ceiling exists it shows ONLY on the holder — so the
     off-holder case is a removal too, and follows the same always-available rule. */
  const ceilingItems = !canAnchor || !isAnchor || (hasCeiling && !isCeiling)
    ? []
    : [{
        label: isCeiling ? 'Remove the CEO ceiling' : 'Set as the CEO ceiling',
        act: 'ceiling',
        mdi: 'mdi-arrow-collapse-up',
        icon: 'vertical_align_top',
      }];

  /* SETTING an anchor needs a completed FULL evaluation — and that is true of
     `review` and `sent-back` just as much as `levelled`. The server's own guard is
     `flow_type = 'STANDARD' AND status = 'COMPLETED'` with no lifecycle condition,
     and levellingStatusKey only ever returns review/sent-back for an evaluation that
     is already COMPLETED (a DRAFT becomes 'in-progress'). So restricting this to
     `levelled` meant a fully-evaluated role sitting in Awaiting Approval could not be
     marked at all — the UI was stricter than the server for no reason. Anchoring is a
     property of the ROLE, governed by levelling_anchor_roles_admin; it is not the
     document's approval state.

     A classified role still has to be re-evaluated first: a classification cannot be
     a calibration reference. */
  const anchorItems = isAnchor
    ? removeAnchorItem
    : !canAnchor ? [] : method === 'evaluate'
      ? [{ label: 'Mark as anchor role', act: 'anchor', mdi: 'mdi-anchor', icon: 'anchor' }]
      : [{ label: 'Evaluate as anchor role', act: 'anchor-evaluate', mdi: 'mdi-anchor', icon: 'anchor' }];

  if (status === 'levelled') {
    return {
      primary: { label: 'Edit level', act: 'edit', variant: 'outlined' },
      more: [
        method === 'classify'
          ? { label: 'Re-classify', act: 'reclassify', mdi: 'mdi-restart', icon: 'restart_alt' }
          : { label: 'Re-evaluate', act: 'evaluate', mdi: 'mdi-restart', icon: 'restart_alt' },
        { label: 'View outcome summary', act: 'outcome', mdi: 'mdi-chart-box-outline', icon: 'assessment' },
        { label: 'View document', act: 'view-doc', mdi: 'mdi-file-document-outline', icon: 'article' },
        ...anchorItems,
        ...ceilingItems,
      ],
    };
  }
  if (status === 'in-progress') {
    return {
      primary: { label: 'Continue', act: 'continue', variant: 'tonal' },
      more: [
        { label: 'Re-assign', act: 'invite', mdi: 'mdi-account-plus-outline', icon: 'person_add' },
        { label: 'View document', act: 'view-doc', mdi: 'mdi-file-document-outline', icon: 'article' },
        ...removeAnchorItem,
        ...ceilingItems,
      ],
    };
  }
  if (status === 'invited') {
    return {
      primary: { label: 'Remind', act: 'remind', variant: 'outlined' },
      more: [
        { label: 'Set level myself', act: 'set-level', mdi: 'mdi-pencil', icon: 'edit' },
        { label: 'Reassign', act: 'invite', mdi: 'mdi-account-plus-outline', icon: 'person_add' },
        { label: 'View document', act: 'view-doc', mdi: 'mdi-file-document-outline', icon: 'article' },
        ...removeAnchorItem,
        ...ceilingItems,
      ],
    };
  }
  if (status === 'review' || status === 'sent-back') {
    return {
      primary: { label: 'Review', act: 'review', variant: 'filled' },
      more: [
        { label: 'View outcome summary', act: 'outcome', mdi: 'mdi-chart-box-outline', icon: 'assessment' },
        { label: 'View document', act: 'view-doc', mdi: 'mdi-file-document-outline', icon: 'article' },
        ...anchorItems,
        ...ceilingItems,
      ],
    };
  }
  /* not-started */
  return {
    primary: { label: 'Set level', act: 'set-level', variant: 'tonal' },
    more: [
      { label: 'Full evaluation', act: 'evaluate', mdi: 'mdi-clipboard-check-outline', icon: 'fact_check' },
      ...(canAnchor && !isAnchor
        ? [{ label: 'Evaluate as anchor role', act: 'anchor-evaluate', mdi: 'mdi-anchor', icon: 'anchor' }]
        : []),
      { label: 'Invite a colleague', act: 'invite', mdi: 'mdi-account-plus-outline', icon: 'person_add' },
      { label: 'View document', act: 'view-doc', mdi: 'mdi-file-document-outline', icon: 'article' },
      ...removeAnchorItem,
      ...ceilingItems,
    ],
  };
}

/** Drop actions the host has no handler for; falls back the primary to the
 *  first supported action so a row always has a working button. */
export function supportedRowActions(actions, supported) {
  const has = (act) => supported.includes(act);
  const more = actions.more.filter((a) => has(a.act));
  if (has(actions.primary.act)) return { primary: actions.primary, more };
  if (!more.length) return { primary: null, more: [] };
  const [next, ...rest] = more;
  return { primary: { label: next.label, act: next.act, variant: 'outlined' }, more: rest };
}
